/**
 * Share Store
 *
 * Manages Pattern B batch sharing state.
 */

import { createWithEqualityFn } from 'zustand/traditional'
import { shallow } from 'zustand/shallow'
import type {
  ShareableItem,
  SharedItem,
  UUID,
} from '../types'
import { sharingApi } from '../api'

interface ShareState {
  selectedItems: ShareableItem[]
  targetCommunityIds: UUID[]
  message: string
  sharedItems: SharedItem[]
  userSharedItems: SharedItem[]

  isSharing: boolean
  isLoading: boolean
  error: string | null

  // Actions
  addItem: (item: ShareableItem) => void
  removeItem: (itemId: UUID) => void
  clearItems: () => void
  setTargetCommunities: (communityIds: UUID[]) => void
  toggleCommunity: (communityId: UUID) => void
  setMessage: (message: string) => void
  executeBatchShare: () => Promise<boolean>
  fetchSharedItems: (communityId: UUID) => Promise<void>
  fetchUserSharedItems: () => Promise<void>
  removeShare: (shareId: UUID) => Promise<void>
  reset: () => void
}

export const useShareStore = createWithEqualityFn<ShareState>((set, get) => ({
  selectedItems: [],
  targetCommunityIds: [],
  message: '',
  sharedItems: [],
  userSharedItems: [],
  isSharing: false,
  isLoading: false,
  error: null,

  addItem: (item) => {
    const { selectedItems } = get()
    if (!selectedItems.find((i) => i.id === item.id)) {
      set({ selectedItems: [...selectedItems, item] })
    }
  },

  removeItem: (itemId) => {
    set({ selectedItems: get().selectedItems.filter((i) => i.id !== itemId) })
  },

  clearItems: () => {
    set({ selectedItems: [] })
  },

  setTargetCommunities: (communityIds) => {
    set({ targetCommunityIds: communityIds })
  },

  toggleCommunity: (communityId) => {
    const { targetCommunityIds } = get()
    if (targetCommunityIds.includes(communityId)) {
      set({ targetCommunityIds: targetCommunityIds.filter((id) => id !== communityId) })
    } else {
      set({ targetCommunityIds: [...targetCommunityIds, communityId] })
    }
  },

  setMessage: (message) => {
    set({ message })
  },

  executeBatchShare: async () => {
    const { selectedItems, targetCommunityIds, message } = get()
    if (selectedItems.length === 0 || targetCommunityIds.length === 0) return false

    set({ isSharing: true, error: null })
    const response = await sharingApi.batchShare({
      items: selectedItems.map((i) => ({ itemId: i.id, itemType: i.type })),
      communityIds: targetCommunityIds,
      message: message || undefined,
    })

    if (response.success) {
      set({
        isSharing: false,
        selectedItems: [],
        targetCommunityIds: [],
        message: '',
      })
      return true
    } else {
      set({ error: response.error ?? 'Failed to share', isSharing: false })
      return false
    }
  },

  fetchSharedItems: async (communityId) => {
    set({ isLoading: true })
    const response = await sharingApi.getSharedItems(communityId)
    if (response.success && response.data) {
      set({ sharedItems: response.data, isLoading: false })
    } else {
      set({ isLoading: false })
    }
  },

  fetchUserSharedItems: async () => {
    set({ isLoading: true })
    const response = await sharingApi.getUserSharedItems()
    if (response.success && response.data) {
      set({ userSharedItems: response.data, isLoading: false })
    } else {
      set({ isLoading: false })
    }
  },

  removeShare: async (shareId) => {
    const response = await sharingApi.removeShare(shareId)
    if (response.success) {
      set({
        sharedItems: get().sharedItems.filter((s) => s.id !== shareId),
        userSharedItems: get().userSharedItems.filter((s) => s.id !== shareId),
      })
    }
  },

  reset: () => {
    set({
      selectedItems: [],
      targetCommunityIds: [],
      message: '',
      sharedItems: [],
      userSharedItems: [],
      isSharing: false,
      isLoading: false,
      error: null,
    })
  },
}))

export const selectSelectedItems = (state: ShareState) => state.selectedItems
export const selectTargetCommunities = (state: ShareState) => state.targetCommunityIds
export const selectIsSharing = (state: ShareState) => state.isSharing
